import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  FileText,
  ArrowUpDown,
  SquareArrowOutUpRight,
  SlidersHorizontal,
} from "lucide-react";
import InfoModal from "@/components/ui/InfoModal";
import DataTable from "@/components/dashboard/DataTable";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
  showApiError,
} from "@/utils";
import apiClient from "@/services/apiClient";
import {
  SHARE_OF_CITATIONS_THRESHOLD_KEY,
  CITATION_WINDOW_KEY,
  URL_CITATION_SHARE_THRESHOLD_KEY,
  DISTANCE_THRESHOLD_KEY,
  DAY_OPTIONS,
  EXPIRY_TIME_IN_MINUTES,
} from "@/const";
import "@/pages/page.css";
import { Modal, Button } from "antd";
import ContentBriefFeedback from "./ContentBriefFeedback";
import SectionFeedback from "./SectionFeedback";
import OptimizeContentFilters from "./OptimizeContentFilters";
import SideBySideEditors from "./SideBySideEditors";
import "./index.css";
import ReviewScore from "./ReviewScore/ReviewScore";
import { fetchRetrievalScores } from "./helpers";
import BrandButton from "@/components/ui/BrandButton";
import SectionHeading from "@/components/ui/SectionHeading";
import useGetQuery from "@/hooks/useGetQuery";
import { BACKEND_URL } from "@/config";

const OptimizeContent = () => {
  const { t } = useTranslation();
  const [shareOfCitationsThreshold, setShareOfCitationsThreshold] = useState(
    () => {
      return getLocalStorageWithExpiry(SHARE_OF_CITATIONS_THRESHOLD_KEY);
    },
  );
  const [urlCitationShareThreshold, setUrlCitationShareThreshold] = useState(
    () => {
      return getLocalStorageWithExpiry(URL_CITATION_SHARE_THRESHOLD_KEY);
    },
  );
  const [citationWindow, setCitationWindow] = useState(() => {
    return getLocalStorageWithExpiry(CITATION_WINDOW_KEY) ?? 7;
  });
  const [distanceThreshold, setDistanceThreshold] = useState(() => {
    return getLocalStorageWithExpiry(DISTANCE_THRESHOLD_KEY);
  });
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const [contentBrief, setContentBrief] = useState("");
  const [contentBriefId, setContentBriefId] = useState("");
  const [contentDraft, setContentDraft] = useState("");
  const [contentDraftHtml, setContentDraftHtml] = useState("");
  // const [contentDraftId, setContentDraftId] = useState("");
  const [_generationTarget, setGenerationTarget] = useState(<></>);
  const [isLoading, setIsLoading] = useState({
    queries: false,
    urls: false,
    relatedQueries: false,
    brief: false,
    draft: false,
    rescore: false,
  });
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [queries, setQueries] = useState([]);
  const [has_ga_distribution, setHas_GA_Distribution] = useState(false);
  const [urls, setUrls] = useState([]);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [funnelStage, setFunnelStage] = useState("all");
  const [queryTypeFilter, setQueryTypeFilter] = useState("all");
  const [topicFilter, setTopicFilter] = useState("all");
  const [selectedQueryGroupId, setSelectedQueryGroupId] = useState("");
  const [selectedQuery, setSelectedQuery] = useState("");
  const [selectedUrl, setSelectedUrl] = useState("");
  const [relatedQueries, setRelatedQueries] = useState([]);
  const [errors, setErrors] = useState({
    genaiQueries: "",
    closestUrls: "",
    relatedQueries: "",
    generateBrief: "",
    generateDraft: "",
  });
  const [isSearchSignalModalVisible, setIsSearchSignalModalVisible] =
    useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [isRegenerateModalVisible, setIsRegenerateModalVisible] =
    useState(false);
  const [isFeedbackSidebarOpen, setIsFeedbackSidebarOpen] = useState(false);
  const [queryScores, setQueryScores] = useState([]);
  const [showReviewScore, setShowReviewScore] = useState(false);
  const lastBackendDraftRef = useRef("");
  const [isConfirmDraftModalVisible, setIsConfirmDraftModalVisible] =
    useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  // Listen for changes in localStorage
  useEffect(() => {
    const handleStorageChange = (event) => {
      try {
        const newValue = JSON.parse(event.newValue);
        if (event.key === SHARE_OF_CITATIONS_THRESHOLD_KEY) {
          setShareOfCitationsThreshold(newValue.value);
        }
        if (event.key === URL_CITATION_SHARE_THRESHOLD_KEY) {
          setUrlCitationShareThreshold(newValue.value);
        }
        if (event.key === DISTANCE_THRESHOLD_KEY) {
          setDistanceThreshold(newValue.value);
        }
      } catch (error) {
        console.error("Error parsing storage change:", error);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Reset generated panels when selected query/url changes
  useEffect(() => {
    setContentBrief("");
    setContentBriefId("");
    setContentDraft("");
    setContentDraftHtml("");
    setShowReviewScore(false);
  }, [selectedQuery, selectedUrl]);

  // Scroll to the content brief when it's generated
  useEffect(() => {
    if (contentBrief) {
      // Small delay to ensure the DOM has updated
      const timerId = setTimeout(() => {
        const contentBriefElement = document.getElementById(
          "content-brief-section",
        );
        if (contentBriefElement) {
          contentBriefElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 200);
      return () => clearTimeout(timerId);
    }
  }, [contentBrief]);

  // Scroll to the content draft when it's generated
  useEffect(() => {
    if (contentDraft) {
      // Small delay to ensure the DOM has updated
      const timerId = setTimeout(() => {
        const contentDraftElement = document.getElementById(
          "content-draft-section",
        );
        if (contentDraftElement) {
          contentDraftElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 200);
      return () => clearTimeout(timerId);
    }
  }, [contentDraft]);

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  // Build cacheable URLs
  const genaiQueriesUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (
      shareOfCitationsThreshold !== undefined &&
      shareOfCitationsThreshold !== null
    ) {
      params.set("share_of_citations_threshold", shareOfCitationsThreshold);
    }
    if (citationWindow !== undefined && citationWindow !== null) {
      params.set("citation_window", citationWindow);
    }
    const qs = params.toString();
    return `${BACKEND_URL}/api/v1/optimize_content/genai-queries${qs ? `?${qs}` : ""}`;
  }, [shareOfCitationsThreshold, citationWindow]);

  const closestUrlEndpoint = useMemo(() => {
    if (!selectedQueryGroupId) return null;
    const params = new URLSearchParams();
    if (
      urlCitationShareThreshold !== undefined &&
      urlCitationShareThreshold !== null
    ) {
      params.set("url_citation_share_threshold", urlCitationShareThreshold);
    }
    if (citationWindow !== undefined && citationWindow !== null) {
      params.set("citation_window", citationWindow);
    }
    const qs = params.toString();
    return `${BACKEND_URL}/api/v1/optimize_content/closest-url?query_group_id=${selectedQueryGroupId}${qs ? `&${qs}` : ""}`;
  }, [selectedQueryGroupId, urlCitationShareThreshold, citationWindow]);

  const relatedQueriesEndpoint = useMemo(() => {
    if (!selectedQueryGroupId) return null;
    const params = new URLSearchParams();
    if (distanceThreshold !== undefined && distanceThreshold !== null) {
      params.set("distance_threshold", distanceThreshold);
    }
    const qs = params.toString();
    return `${BACKEND_URL}/api/v1/optimize_content/related-queries?query_group_id=${selectedQueryGroupId}${qs ? `&${qs}` : ""}`;
  }, [selectedQueryGroupId, distanceThreshold]);

  // React Query-backed fetches (cached for 5 minutes per unique URL)
  const [genaiQueriesResp, genaiQueriesIsLoaded, genaiQueriesError] =
    useGetQuery(genaiQueriesUrl);
  // staleTime: 0 so revisiting the same prompt always triggers a background
  // refetch, ensuring the user sees fresh closest-url and related-queries data.
  const [closestUrlResp, closestUrlIsLoaded, closestUrlError] = useGetQuery(
    closestUrlEndpoint,
    { staleTime: 0 },
  );
  const [relatedQueriesResp, relatedQueriesIsLoaded, relatedQueriesError] =
    useGetQuery(relatedQueriesEndpoint, { staleTime: 0 });

  // Sync genai-queries result into component state
  useEffect(() => {
    // Stop loading when the request settles (success or error)
    setIsLoading((prev) => ({
      ...prev,
      queries: !genaiQueriesIsLoaded && !genaiQueriesError,
    }));
    if (genaiQueriesResp) {
      const data = (genaiQueriesResp.data ?? []).map((q) => ({
        ...q,
        share_of_citations_display: `${q.own_domain_citations} / ${q.total_citations}`,
      }));
      setQueries(data);
      setHas_GA_Distribution(genaiQueriesResp.has_ga_distribution ?? false);
    }
    if (genaiQueriesError) {
      setErrors((prev) => ({ ...prev, genaiQueries: genaiQueriesError }));
    }
  }, [genaiQueriesResp, genaiQueriesIsLoaded, genaiQueriesError]);

  // Sync closest-url result into component state
  useEffect(() => {
    // Stop loading when the request settles (success or error)
    setIsLoading((prev) => ({
      ...prev,
      urls: !!selectedQuery && !closestUrlIsLoaded && !closestUrlError,
    }));
    setUrls(closestUrlResp?.data ?? []);
    if (closestUrlError) {
      setErrors((prev) => ({ ...prev, closestUrls: closestUrlError }));
    }
  }, [closestUrlResp, closestUrlIsLoaded, closestUrlError, selectedQuery]);

  // Sync related-queries result into component state
  useEffect(() => {
    // Stop loading when the request settles (success or error)
    setIsLoading((prev) => ({
      ...prev,
      relatedQueries:
        !!selectedQuery && !relatedQueriesIsLoaded && !relatedQueriesError,
    }));
    setRelatedQueries(relatedQueriesResp?.data?.map((q) => q.query) ?? []);
    if (relatedQueriesError) {
      setErrors((prev) => ({ ...prev, relatedQueries: relatedQueriesError }));
    }
  }, [
    relatedQueriesResp,
    relatedQueriesIsLoaded,
    relatedQueriesError,
    selectedQuery,
  ]);

  const handleSort = useCallback(
    (key) => {
      // default: asc
      let direction = "asc";
      // if already sorted by this key, toggle direction
      if (sortConfig.key === key && sortConfig.direction === "asc") {
        direction = "desc";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig],
  );

  const sortData = (data, config) => {
    // shallow copy to avoid mutating original data
    return [...data].sort((a, b) => {
      const { key, direction } = config;
      if (!key) return 0;
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const columnHeader = useCallback(
    (label, sortKey, info, displayJSX) => (
      <div
        className="d-flex align-items-center gap-1 cursor-pointer col-sort-header"
        style={{ cursor: "pointer" }}
      >
        {displayJSX ?? label}
        {info && <InfoModal label={label} info={info} />}
        <ArrowUpDown
          size={14}
          className="text-secondary sort-arrow-icon"
          onClick={() => handleSort(sortKey)}
          data-testid={`sort-arrow-${sortKey}`}
        />
      </div>
    ),
    [handleSort],
  );

  const queryColumns = useMemo(
    () => [
      {
        key: "query",
        label: t("contentOptimizer.prompt", "Prompt"),
        header: columnHeader(
          t("contentOptimizer.prompt", "Prompt"),
          "query",
          t(
            "contentOptimizer.promptTooltip",
            "Sample prompt - representative of similar prompts analyzed",
          ),
        ),
        width: "35%",
        render: (value) => <span className="fw-medium text-dark">{value}</span>,
      },
      {
        key: "cluster_size",
        label: t("contentOptimizer.clusterSize", "Cluster Size"),
        header: columnHeader(
          t("contentOptimizer.clusterSize", "Cluster Size"),
          "cluster_size",
          t(
            "contentOptimizer.clusterSizeTooltip",
            "Number of prompts similar to this one",
          ),
        ),
        width: "15%",
      },
      {
        key: has_ga_distribution ? "search_signal" : "",
        label: t("contentOptimizer.searchSignal", "Search Signal"),
        header: columnHeader(
          t("contentOptimizer.searchSignal", "Search Signal"),
          has_ga_distribution ? "search_signal" : "",
          t(
            "contentOptimizer.searchSignalTooltip",
            "Relative search prominence based on Google Analytics Data",
          ),
          has_ga_distribution ? null : (
            <div
              className="d-flex flex-column align-items-center"
              style={{ minWidth: 0 }}
            >
              <span
                className="fw-medium text-dark"
                style={{ whiteSpace: "nowrap", display: "block" }}
              >
                {t("contentOptimizer.searchSignal", "Search Signal")}
              </span>
              <button
                className="btn btn-link p-0 mt-1"
                style={{
                  color: "var(--brand-color-1)",
                  textDecoration: "underline",
                  whiteSpace: "nowrap",
                }}
                onClick={() => setIsSearchSignalModalVisible(true)}
              >
                <small>{t("contentOptimizer.whyIsEmpty", "Why is this empty?")}</small>
              </button>
            </div>
          ),
        ),
        width: "15%",
        render: (value) => {
          return (
            <span className="fw-medium">
              {value != null ? Math.round(value).toLocaleString() : ""}
            </span>
          );
        },
      },
      {
        key: "share_of_citations_display",
        label: t("contentOptimizer.shareOfCitations", "Share of Citations"),
        header: columnHeader(
          t("contentOptimizer.shareOfCitations", "Share of Citations"),
          "share_of_citations",
          t(
            "contentOptimizer.shareOfCitationsTooltip",
            "Your content's current visibility in AI search results. Example: '8 / 10' means that 8 out of 10 total citations for this prompt are from your website.",
          ),
        ),
        width: "20%",
        render: (value) => {
          if (typeof value !== "string" || !value.includes(" / ")) {
            return (
              <div className="d-flex align-items-center">
                <span className="fw-medium text-dark">—</span>
              </div>
            );
          }

          const [own_domain_citations, total_citations] = value
            .split(" / ")
            .map(Number);
          if (isNaN(own_domain_citations) || isNaN(total_citations)) {
            return (
              <div className="d-flex align-items-center">
                <span className="fw-medium text-dark">—</span>
              </div>
            );
          }
          let ratio =
            total_citations === 0 ? 0 : own_domain_citations / total_citations;
          return (
            <div className="d-flex align-items-center">
              <span
                className={`fw-medium ${ratio >= 0.5 ? "text-success" : "text-dark"}`}
              >
                {value}
              </span>
            </div>
          );
        },
      },
      {
        key: "top_retrieval_score",
        label: t("contentOptimizer.topRetrievalScore", "Top Retrieval Score"),
        header: columnHeader(
          t("contentOptimizer.topRetrievalScore", "Top Retrieval Score"),
          "top_retrieval_score",
          t(
            "contentOptimizer.topRetrievalScoreTooltip",
            "The highest retrieval score achieved by any URL for all queries in this query group",
          ),
        ),
        width: "15%",
        render: (value) => (
          <div className="d-flex align-items-center">
            <span className="fw-medium text-dark">
              {value != null ? (value * 100).toFixed(0) : "—"}
            </span>
          </div>
        ),
      },
    ],
    [has_ga_distribution, columnHeader, t],
  );

  const urlColumns = useMemo(
    () => [
      {
        key: "url",
        label: t("contentOptimizer.page", "Page"),
        header: columnHeader(
          t("contentOptimizer.page", "Page"),
          "url",
          t(
            "contentOptimizer.pageTooltip",
            "Pages containing content that comes closest to addressing the prompt, and so are likely to require the least effort to update",
          ),
        ),
        width: "75%",
        render: (url) => (
          <div className="fw-medium">
            {url.length > 99 ? url.substring(0, 99) + "..." : url}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none url-icon-link"
            >
              <SquareArrowOutUpRight
                size={18}
                className="ms-1"
                style={{ color: "var(--brand-color-1)" }}
              />
            </a>
          </div>
        ),
      },
      {
        key: "n_citations",
        label: t("contentOptimizer.citations", "Citations"),
        header: columnHeader(
          t("contentOptimizer.citations", "Citations"),
          "n_citations",
          t(
            "contentOptimizer.citationsTooltip",
            "How many citations (links) this URL currently has for the prompt",
          ),
        ),
        width: "12%",
        render: (value) => (
          <div className="d-flex align-items-center">
            <span className="fw-medium text-dark">{value}</span>
          </div>
        ),
      },
      {
        key: "retrieval_score",
        label: t("contentOptimizer.retrievalScore", "Retrieval Score"),
        header: columnHeader(
          t("contentOptimizer.retrievalScore", "Retrieval Score"),
          "retrieval_score",
          t(
            "contentOptimizer.retrievalScoreTooltip",
            "A composite score indicating how well the page answers the prompt",
          ),
        ),
        width: "13%",
        render: (value) => (
          <div className="d-flex align-items-center">
            <span className="fw-medium text-dark">
              {value != null ? (value * 100).toFixed(0) : "—"}
            </span>
          </div>
        ),
      },
    ],
    [columnHeader, t],
  );

  const topicOptions = useMemo(() => {
    const topics = [
      ...new Set(queries.map((q) => q.topic).filter(Boolean)),
    ].sort();
    return [
      { value: "all", label: t("common.all", "All") },
      ...topics.map((t) => ({ value: t, label: t })),
    ];
  }, [queries, t]);

  // Queries filtered by brand/funnel/topic/search only — used to determine which
  // query types are actually available given the current filters (excludes
  // queryTypeFilter so the dropdown options stay accurate).
  const queriesForTypeFilter = useMemo(() => {
    return queries.filter(
      (q) =>
        q.query.toLowerCase().includes(search.toLowerCase()) &&
        (brandFilter === "all" ||
          (brandFilter === "branded" && q.is_branded) ||
          (brandFilter === "unbranded" && !q.is_branded)) &&
        (funnelStage === "all" || q.funnel_stage === funnelStage) &&
        (topicFilter === "all" || q.topic === topicFilter),
    );
  }, [queries, search, brandFilter, funnelStage, topicFilter]);

  // Auto-reset queryTypeFilter when the selected type is no longer present in
  // the current brand/funnel/search-filtered set.
  useEffect(() => {
    if (queryTypeFilter === "all") return;
    const hasReal = queriesForTypeFilter.some(
      (q) => q.query_type === "real" || q.query_type === "ai_summary",
    );
    const hasSynthetic = queriesForTypeFilter.some(
      (q) => q.query_type === "synthetic",
    );
    const hasAdhoc = queriesForTypeFilter.some((q) => q.query_type === "adhoc");
    if (
      (queryTypeFilter === "real" && !hasReal) ||
      (queryTypeFilter === "synthetic" && !hasSynthetic) ||
      (queryTypeFilter === "adhoc" && !hasAdhoc)
    ) {
      setQueryTypeFilter("all");
    }
  }, [queriesForTypeFilter, queryTypeFilter]);

  const filteredQueries = useMemo(() => {
    // First sort the queries, then filter by:
    // - Search term: query contains the search string (case-insensitive)
    // - Brand filter: 'all' shows all queries, 'branded' shows only queries where is_branded is true, 'unbranded' shows only where is_branded is false
    // - Funnel stage: 'all' shows all queries, otherwise matches the selected funnel_stage
    // - Query type: 'real' matches query_type==="real" or "ai_summary", 'synthetic' matches query_type==="synthetic", 'adhoc' matches query_type==="adhoc", 'all' shows all
    const sortedQueries = sortData(queries, sortConfig);
    return sortedQueries.filter(
      (q) =>
        q.query.toLowerCase().includes(search.toLowerCase()) &&
        (brandFilter === "all" ||
          (brandFilter === "branded" && q.is_branded) ||
          (brandFilter === "unbranded" && !q.is_branded)) &&
        (funnelStage === "all" || q.funnel_stage === funnelStage) &&
        (topicFilter === "all" || q.topic === topicFilter) &&
        (queryTypeFilter === "all" ||
          (queryTypeFilter === "real" &&
            (q.query_type === "real" || q.query_type === "ai_summary")) ||
          (queryTypeFilter === "synthetic" && q.query_type === "synthetic") ||
          (queryTypeFilter === "adhoc" && q.query_type === "adhoc")),
    );
  }, [
    search,
    queries,
    sortConfig,
    brandFilter,
    funnelStage,
    topicFilter,
    queryTypeFilter,
  ]);

  const filteredUrls = useMemo(() => {
    const sortedUrls = sortData(urls, sortConfig);
    return sortedUrls.filter((u) => u.is_own_brand);
  }, [urls, sortConfig]);

  const topPerformingCompetitorUrls = useMemo(() => {
    return urls
      .filter((u) => u.is_competitor_brand)
      .sort((a, b) => b.match - a.match)
      .slice(0, 5);
  }, [urls]);

  const topWebSources = useMemo(() => {
    return urls
      .filter((u) => !u.is_own_brand && !u.is_competitor_brand)
      .sort((a, b) => b.match - a.match)
      .slice(0, 5);
  }, [urls]);

  useEffect(() => {
    // Clear the current selection if it is no longer present in the filtered list
    if (!selectedQuery) {
      return;
    }
    const isSelectedQueryVisible = filteredQueries.some(
      (q) => q.query === selectedQuery,
    );
    if (!isSelectedQueryVisible) {
      setSelectedQuery("");
      setSelectedQueryGroupId("");
    }
  }, [filteredQueries, selectedQuery]);

  useEffect(() => {
    const handleGenerationTarget = () => {
      setGenerationTarget(
        <>
          {selectedUrl ? (
            <p className="small text-muted mb-3" data-testid="targeting-copy">
              {t("contentOptimizer.targetingPage", "Targeting the page")}{" "}
              <a
                data-testid="targeting-link"
                href={selectedUrl}
                className="text-brand"
              >
                {selectedUrl}
              </a>{" "}
              {t("contentOptimizer.toBetterAnswerPrompt", "to better answer the prompt")} <strong>"{selectedQuery}"</strong>
            </p>
          ) : (
            <p className="small text-muted mb-3" data-testid="targeting-copy">
              {t("contentOptimizer.newPageToAnswerPrompt", "New page to answer the prompt")} <strong>"{selectedQuery}"</strong>
            </p>
          )}

          <div className="mb-3" data-testid="related-queries-section">
            <h5 className="small fw-medium text-dark mb-2">
              {t("contentOptimizer.relatedQueries", "Related Prompts")}
            </h5>
            <div className="mb-3">
              {relatedQueries.length > 0 ? (
                relatedQueries.map((query, index) => (
                  <div
                    key={index}
                    className="small text-dark"
                    data-testid="related-query-item"
                  >
                    {query}
                  </div>
                ))
              ) : (
                <div
                  className="small text-secondary"
                  data-testid="related-queries-empty"
                >
                  {t(
                    "contentOptimizer.noRelatedPromptsFound",
                    "No related prompts found",
                  )}
                </div>
              )}
            </div>
            <SectionFeedback
              section="related_queries"
              isFrozen={isLoading.relatedQueries}
            />
          </div>

          <hr />

          <div className="mb-3">
            <h5 className="small fw-medium text-dark mb-2">
              {t(
                "contentOptimizer.topCompetitorPages",
                "Top Performing Competitor Pages",
              )}
            </h5>
            <ul className="list-unstyled" data-testid="top-competitor-list">
              {topPerformingCompetitorUrls.length > 0 ? (
                topPerformingCompetitorUrls.map((u) => (
                  <li key={u.url} className="small">
                    {u.match}% -{" "}
                    <a
                      href={u.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand"
                    >
                      {u.url}
                    </a>
                  </li>
                ))
              ) : (
                <li
                  className="small text-secondary"
                  data-testid="top-competitor-empty"
                >
                  {t(
                    "contentOptimizer.noCompetitorPagesFound",
                    "No competitor pages found",
                  )}
                </li>
              )}
            </ul>
            <SectionFeedback
              section="top_competitor_pages"
              isFrozen={isLoading.urls}
            />
          </div>

          <hr />

          <div className="mb-3">
            <h5 className="small fw-medium text-dark mb-2">
              {t("contentOptimizer.topWebSources", "Top Web Sources")}
            </h5>
            <ul className="list-unstyled" data-testid="top-web-sources-list">
              {topWebSources.length > 0 ? (
                topWebSources.map((u) => (
                  <li key={u.url} className="small">
                    {u.match}% -{" "}
                    <a
                      href={u.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand"
                    >
                      {u.url}
                    </a>
                  </li>
                ))
              ) : (
                <li
                  className="small text-secondary"
                  data-testid="top-web-sources-empty"
                >
                  {t(
                    "contentOptimizer.noWebSourcesFound",
                    "No web sources found",
                  )}
                </li>
              )}
            </ul>
            <SectionFeedback
              section="top_web_sources"
              isFrozen={isLoading.urls}
            />
          </div>
        </>,
      );
    };
    handleGenerationTarget();
  }, [
    selectedUrl,
    selectedQuery,
    relatedQueries,
    topPerformingCompetitorUrls,
    topWebSources,
    isLoading,
    t,
  ]);

  useEffect(() => {
    setQueryScores([
      { query: selectedQuery, oldScore: 0, newScore: 0 },
      ...relatedQueries.map((q) => ({ query: q, oldScore: 0, newScore: 0 })),
    ]);
  }, [relatedQueries, selectedQuery, selectedUrl]);

  const updateQueryScores = async (targetPageContent, useTopScore = false) => {
    const result = await fetchRetrievalScores(
      [selectedQuery, ...relatedQueries],
      targetPageContent,
      BACKEND_URL,
    );
    if (!result || !result.scores) {
      console.warn("Query scoring skipped; retrieval scores are null or undefined.");
      return;
    }
    const selectedQueryTopScore = useTopScore
      ? queries.find((q) => q.query === selectedQuery)?.top_retrieval_score
      : undefined;
    const scoreMap = new Map(
      result.scores.map((s) => [
        s.query,
        useTopScore &&
        s.query === selectedQuery &&
        selectedQueryTopScore != null
          ? selectedQueryTopScore
          : s.retrieval_score,
      ]),
    );
    setQueryScores((prev) =>
      prev.map((qs) => {
        const newScore = scoreMap.get(qs.query);
        return newScore !== undefined
          ? { ...qs, oldScore: qs.newScore, newScore }
          : qs;
      }),
    );
  };

  const handleGenerateBrief = () => {
    setContentDraft("");
    setIsLoading((prev) => ({ ...prev, brief: true }));
    apiClient
      .post(`${BACKEND_URL}/api/v1/optimize_content/generate-content-brief`, {
        target_url: selectedUrl,
        target_query: selectedQuery,
        related_queries: relatedQueries,
        top_competitor_pages: topPerformingCompetitorUrls.map((u) => u.url),
        top_web_sources: topWebSources.map((u) => u.url),
      })
      .then((res) => res.data)
      .then(async (resp) => {
        setContentBrief(resp.content_brief);
        setContentBriefId(resp.content_brief_id);
        setContentDraft(resp.target_page_content);
        lastBackendDraftRef.current = resp.target_page_content ?? "";
        if (resp.target_page_content) {
          await updateQueryScores(resp.target_page_content, true);
        }
      })
      .catch((err) => {
        console.error("Error generating content brief:", err);
        showApiError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t(
              "contentOptimizer.generateBriefError",
              "Failed to generate content brief. Please try again.",
            ),
        );
        setErrors((prev) => ({ ...prev, generateBrief: err }));
      })
      .finally(() => {
        setIsLoading((prev) => ({ ...prev, brief: false }));
      });
  };

  const handleRegenerateBrief = () => {
    setFeedbacks([]);
    setContentDraft("");
    setIsFeedbackSidebarOpen(false);
    setIsLoading((prev) => ({ ...prev, brief: true }));
    apiClient
      .post(
        `${BACKEND_URL}/api/v1/optimize_content/regenerate-content-brief-with-feedback`,
        { feedbacks },
      )
      .then((res) => res.data)
      .then((resp) => {
        setContentBrief(resp.content_brief);
        setContentBriefId(resp.content_brief_id);
        setContentDraft(resp.target_page_content);
        lastBackendDraftRef.current = resp.target_page_content ?? "";
      })
      .catch((err) => {
        console.error("Error generating content brief:", err);
        showApiError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t(
              "contentOptimizer.regenerateBriefError",
              "Failed to regenerate content brief. Please try again.",
            ),
        );
        setErrors((prev) => ({ ...prev, generateBrief: err }));
      })
      .finally(() => setIsLoading((prev) => ({ ...prev, brief: false })));
  };

  const doGenerateDraft = () => {
    setIsLoading((prev) => ({ ...prev, draft: true }));
    apiClient
      .post(`${BACKEND_URL}/api/v1/optimize_content/generate-content-draft`, {
        target_url: selectedUrl,
        target_query: selectedQuery,
        content_brief_id: contentBriefId,
      })
      .then((res) => res.data)
      .then((resp) => {
        setContentDraft(resp.content_draft);
        lastBackendDraftRef.current = resp.content_draft ?? "";
      })
      .catch((err) => {
        console.error("Error generating content draft:", err);
        showApiError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t(
              "contentOptimizer.generateDraftError",
              "Failed to generate content draft. Please try again.",
            ),
        );
        setErrors((prev) => ({ ...prev, generateDraft: err }));
      })
      .finally(() => setIsLoading((prev) => ({ ...prev, draft: false })));
  };

  const handleGenerateDraft = () => {
    if (contentDraft && contentDraft !== lastBackendDraftRef.current) {
      setIsConfirmDraftModalVisible(true);
    } else {
      doGenerateDraft();
    }
  };

  const handleRescoreDraft = async () => {
    setIsLoading((prev) => ({ ...prev, rescore: true }));
    await updateQueryScores(contentDraft);
    setShowReviewScore(true);
    setIsLoading((prev) => ({ ...prev, rescore: false }));
  };

  // Auto-scroll to the ReviewScore panel when it's shown
  useEffect(() => {
    if (showReviewScore) {
      // Small delay to ensure the DOM has rendered the ReviewScore component
      const timerId = setTimeout(() => {
        const el = document.querySelector('[data-testid="review-score"]');
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 200);
      return () => clearTimeout(timerId);
    }
  }, [showReviewScore]);

  const saveCitationWindow = (value) => {
    setCitationWindow(value);
    setLocalStorageWithExpiry(
      CITATION_WINDOW_KEY,
      value,
      EXPIRY_TIME_IN_MINUTES,
    );
  };

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"optimize-content"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />
          {contentBrief && (
            <ContentBriefFeedback
              content_brief_id={contentBriefId}
              feedbacks={feedbacks}
              setFeedbacks={setFeedbacks}
              sidebarOpen={isFeedbackSidebarOpen}
              setSidebarOpen={setIsFeedbackSidebarOpen}
            />
          )}
          {/* Heading  */}
          <div className="mb-4">
            <h3 className="h4 fw-bold text-dark">
              {t("contentOptimizer.title", "Optimize Content")}
            </h3>
            <p className="mt-1 fs-6 text-secondary">
              {t(
                "contentOptimizer.subtitle",
                "This page helps identify existing content that could be improved to better serve user prompts.",
              )}
            </p>
          </div>
          {/* Tables  */}
          <div className="row g-4">
            {/* GenAI Queries Table */}
            <div className="col-12 col-lg-6" data-tour="optimize-content.prompts">
              <div className="section-header d-flex align-items-center justify-content-between mb-3">
                <SectionHeading>
                  {t("contentOptimizer.promptsHeading", "GenAI Prompts")}
                </SectionHeading>
                <SectionFeedback
                  section="genai_queries"
                  isFrozen={
                    isLoading.genaiQueries || filteredQueries.length === 0
                  }
                />
              </div>
              <div className="card-body">
                {/* Search Box + Filters button */}
                <div className="mb-3">
                  <div className="d-flex align-items-center gap-2 mb-3" data-tour="optimize-content.filters">
                    <div className="position-relative flex-grow-1">
                      <Search
                        size={18}
                        className="position-absolute top-50 start-0 translate-middle-y text-secondary ms-3"
                      />
                      <input
                        type="text"
                        placeholder={t(
                          "promptLibrary.searchPlaceholder",
                          "Search prompts...",
                        )}
                        value={search}
                        className="form-control ps-5"
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary d-flex align-items-center gap-2 flex-shrink-0"
                      onClick={() => setIsFiltersModalOpen(true)}
                      data-testid="filters-modal-btn"
                    >
                      <SlidersHorizontal size={16} />
                      {t("contentOptimizer.filters", "Filters")}
                    </button>
                  </div>
                </div>
                {/* Filters Modal */}
                <Modal
                  title={t("contentOptimizer.filters", "Filters")}
                  open={isFiltersModalOpen}
                  onCancel={() => setIsFiltersModalOpen(false)}
                  width={680}
                  footer={[
                    <Button
                      key="done"
                      data-testid="filters-modal-done-btn"
                      onClick={() => setIsFiltersModalOpen(false)}
                      style={{
                        backgroundColor: "var(--brand-color-1)",
                        color: "#ffffff",
                        border: "none",
                      }}
                    >
                      {t("common.done", "Done")}
                    </Button>,
                  ]}
                >
                  <div style={{ paddingTop: "8px" }}>
                    <OptimizeContentFilters
                      brandFilter={brandFilter}
                      setBrandFilter={setBrandFilter}
                      funnelStage={funnelStage}
                      setFunnelStage={setFunnelStage}
                      queryTypeFilter={queryTypeFilter}
                      setQueryTypeFilter={setQueryTypeFilter}
                      topicFilter={topicFilter}
                      setTopicFilter={setTopicFilter}
                      topicOptions={topicOptions}
                      citationWindow={citationWindow}
                      saveCitationWindow={saveCitationWindow}
                      periodOptions={DAY_OPTIONS}
                      isLoadingPeriod={isLoading.brief || isLoading.draft}
                      queries={queriesForTypeFilter}
                    />
                  </div>
                </Modal>
                <DataTable
                  columns={queryColumns}
                  data={filteredQueries}
                  idKey={"query"}
                  onRowClick={(item) => {
                    setSelectedQuery(item.query);
                    setSelectedQueryGroupId(item.query_group_id);
                  }}
                  isLoaded={!isLoading.queries}
                  isFrozen={
                    isLoading.brief || isLoading.draft || isLoading.rescore
                  }
                  error={errors.genaiQueries}
                  emptyStateMessage={
                    shareOfCitationsThreshold > 0 ? (
                      <div>
                        {t(
                          "contentOptimizer.noPromptsFoundWithThresholdPart1",
                          'No prompts found. Try lowering the "Share of Citations threshold" from the ',
                        )}
                        <a
                          href="/settings"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand"
                        >
                          {t("userMenu.settings", "settings")}
                        </a>
                        {t("contentOptimizer.noPromptsFoundWithThresholdPart2", ".")}
                      </div>
                    ) : (
                      <div>
                        {t(
                          "contentOptimizer.noPromptsFoundContactSupport",
                          "No prompts found. Please contact support at tech.support@elelem.ai.",
                        ).split("tech.support@elelem.ai")[0]}
                        <a
                          href="mailto:tech.support@elelem.ai"
                          className="text-brand"
                        >
                          tech.support@elelem.ai
                        </a>
                        {t(
                          "contentOptimizer.noPromptsFoundContactSupport",
                          "No prompts found. Please contact support at tech.support@elelem.ai.",
                        ).split("tech.support@elelem.ai")[1]}
                      </div>
                    )
                  }
                  config={{ cardPadding: "", cardHeight: "600px" }}
                  // exportFilename="GenAI_Prompts"
                />
              </div>
            </div>
            {/* Closest URLs Table */}
            <div className="col-12 col-lg-6" data-tour="optimize-content.pages">
              <div className="section-header d-flex align-items-center justify-content-between mb-3">
                <SectionHeading>
                  {t(
                    "contentOptimizer.pagesHeading",
                    "Pages that best answer this prompt",
                  )}
                </SectionHeading>
                <SectionFeedback
                  section="closest_urls"
                  isFrozen={isLoading.closestUrls || urls.length === 0}
                />
              </div>
              <div className="card-body">
                {selectedQuery ? (
                  <>
                    <div className="mb-3 d-flex align-items-center justify-content-end">
                      <button
                        className="btn btn-outline-secondary w-auto fw-medium clear-url-btn"
                        style={{ fontSize: "0.95rem" }}
                        onClick={() => setSelectedUrl("")}
                        disabled={
                          !selectedUrl || isLoading.brief || isLoading.draft
                        }
                      >
                        {t("contentOptimizer.clearUrl", "Clear URL")}
                      </button>
                    </div>
                    <DataTable
                      columns={urlColumns}
                      data={filteredUrls}
                      idKey={"url"}
                      onRowClick={(item) => setSelectedUrl(item.url)}
                      isLoaded={!isLoading.urls}
                      isFrozen={
                        isLoading.brief || isLoading.draft || isLoading.rescore
                      }
                      error={errors.closestUrls}
                      emptyStateMessage={
                        urlCitationShareThreshold > 0 ? (
                          <div>
                            {
                              'No pages found. Try lowering the "match threshold" from the '
                            }
                            <a
                              href="/settings"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand"
                            >
                              settings
                            </a>
                            .
                          </div>
                        ) : (
                          <div>
                            You do not have any citations for this prompt within
                            the selected time period. Extend the period or try
                            creating a new page targeting it.
                          </div>
                        )
                      }
                      removeSelection={selectedUrl === ""}
                      config={{ cardPadding: "", cardHeight: "550px" }}
                      // exportFilename="Matching_Pages"
                    />
                    <div className="mt-3" data-tour="optimize-content.generate-brief">
                      <BrandButton
                        onClick={handleGenerateBrief}
                        disabled={isLoading.brief}
                        className={"w-100"}
                        dataTestId="generate-brief-button"
                      >
                        {isLoading.brief ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            {t(
                              "contentOptimizer.generatingBrief",
                              "Generating Brief...",
                            )}
                          </>
                        ) : selectedUrl ? (
                          t(
                            "contentOptimizer.generateImprovementBrief",
                            "Generate Improvement Brief",
                          )
                        ) : (
                          t(
                            "contentOptimizer.generateNewPageBrief",
                            "Generate New Page Brief",
                          )
                        )}
                      </BrandButton>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <FileText
                      data-testid="initial-empty-url-icon"
                      size={48}
                      className="mx-auto text-secondary mb-2"
                    />
                    <p
                      className="text-muted"
                      data-testid="initial-empty-url-text"
                    >
                      {t(
                        "contentOptimizer.selectPromptToSeeUrls",
                        "Select a prompt to see matching URLs",
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Content Brief  */}
          <div
            id="content-brief-section"
            style={{ minHeight: "65px" /* topnavbar + 5px */ }}
          >
            {" "}
          </div>

          <div
            id="content-draft-section"
            style={{ minHeight: "65px" /* topnavbar + 5px */ }}
          ></div>
          {contentBrief && (
            <div className="mt-4">
              <SideBySideEditors
                feedbacks={feedbacks}
                isLoadingBrief={isLoading.brief}
                isRegenerateModalVisible={isRegenerateModalVisible}
                setIsRegenerateModalVisible={setIsRegenerateModalVisible}
                handleRegenerateBrief={handleRegenerateBrief}
                contentBrief={contentBrief}
                contentDraft={contentDraft}
                setContentDraft={setContentDraft}
                onHtmlValueChange={setContentDraftHtml}
              />
              {/* Button */}
              <div className="mt-3">
                <div className="d-flex gap-3" data-tour="optimize-content.draft-actions">
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <BrandButton
                      onClick={handleGenerateDraft}
                      disabled={isLoading.draft}
                      className={""}
                      dataTestId="suggest-draft-button"
                    >
                      {isLoading.draft ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          {t(
                            "contentOptimizer.generatingDraft",
                            "Generating Draft...",
                          )}
                        </>
                      ) : (
                        t(
                          "contentOptimizer.suggestDraft",
                          "Suggest Draft ✨",
                        )
                      )}
                    </BrandButton>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                    }}
                  >
                    <BrandButton
                      onClick={handleRescoreDraft}
                      disabled={
                        isLoading.rescore ||
                        !contentDraftHtml
                          ?.replace(/<[^>]*>/g, "")
                          .replace(/&nbsp;/g, " ")
                          .trim()
                      }
                      className={"ms-0"}
                      dataTestId="rescore-draft-button"
                    >
                      {isLoading.rescore ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          {t(
                            "contentOptimizer.calculatingScore",
                            "Rescoring Draft...",
                          )}
                        </>
                      ) : (
                        t(
                          "contentOptimizer.rescoreDraft",
                          "Rescore Draft",
                        )
                      )}
                    </BrandButton>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showReviewScore && (
            <div className="mt-4">
              <ReviewScore
                queryScores={queryScores}
                contentDraft={contentDraft}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
      </main>
      <Modal
        title={t("contentOptimizer.confirmDraftOverwriteTitle", "Overwrite your edits?")}
        open={isConfirmDraftModalVisible}
        onCancel={() => setIsConfirmDraftModalVisible(false)}
        footer={[
          <BrandButton
            key="confirm"
            onClick={() => {
              setIsConfirmDraftModalVisible(false);
              doGenerateDraft();
            }}
          >
            {t("contentOptimizer.generateDraft", "Generate Draft")}
          </BrandButton>,
        ]}
      >
        <p>
          {t(
            "contentOptimizer.confirmDraftOverwriteText",
            "You have unsaved edits in the Content Editor. Generating a new draft will overwrite them. Do you want to continue?",
          )}
        </p>
      </Modal>
      <Modal
        title={t("contentOptimizer.searchSignalModalTitle", "Why is Search Signal empty?")}
        open={isSearchSignalModalVisible}
        onCancel={() => setIsSearchSignalModalVisible(false)}
        footer={null}
      >
        <p>
          {t(
            "contentOptimizer.searchSignalModalText1",
            "We need your Google Analytics data to compute the {{searchSignal}} of your prompts.",
            { searchSignal: t("contentOptimizer.searchSignal", "Search Signal") },
          )}
        </p>
        <div style={{ textAlign: "center", marginTop: "16px" }}>
          <img
            src="https://cdn.geo.elelem.ai/assets/example-GA-spreadsheet.png"
            alt="Example GA Spreadsheet"
            style={{
              maxWidth: "100%",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          />
        </div>
        <p>
          <br />
          {t(
            "contentOptimizer.searchSignalModalText2",
            "Send us an email at tech-support@elelem.ai with your Google Analytics spreadsheet to enable this feature.",
            { email: "tech-support@elelem.ai" },
          ).split("tech-support@elelem.ai")[0]}
          <a href="mailto:tech-support@elelem.ai" className="text-brand">
            tech-support@elelem.ai
          </a>
          {t(
            "contentOptimizer.searchSignalModalText2",
            "Send us an email at tech-support@elelem.ai with your Google Analytics spreadsheet to enable this feature.",
            { email: "tech-support@elelem.ai" },
          ).split("tech-support@elelem.ai")[1]}
        </p>
      </Modal>
    </div>
  );
};

export default OptimizeContent;
